import axios from "axios";
import { ALMI_BASE_URL, AUTH_BASE_URL } from "./constants";

export interface LoginResponse {
  token: string;
}

export interface BackendProject {
  initial: string;
  ip: string;
}

export interface BackendServer {
  alfan: string;
}

export interface Project {
  id: string;
  initial: string;
  alfan: string;
}

export interface Server {
  id: string;
  alfan: string;
}

export interface ProjectsDto {
  server: string;
  forceMove?: boolean;
  dryRun?: boolean;
  filtered?: string[];
  username?: string;
}

export interface ServerStatusResponse {
  success: boolean;
  status?: "up" | "down";
  reason?: string;
  dryRun?: boolean;
  failures?: number;
  alive?: Array<{ domain: string; status: number }>;
  errors?: Array<{ domain: string; err: string }>;
  isDown?: boolean;
}

export interface RecoveryJobResponse extends ServerStatusResponse {
  jobId: string;
  error?: string;
}

const getUsername = (): string | null => {
  return localStorage.getItem("auth_username");
};

const api = axios.create({ baseURL: AUTH_BASE_URL, timeout: 10000 });
const almiApi = axios.create({ baseURL: ALMI_BASE_URL, timeout: 10000 });

const ATTACHED_SYMBOL = Symbol("authInterceptorAttached");

const attachAuth = (instance: typeof api | typeof almiApi) => {
  if ((instance as any)[ATTACHED_SYMBOL]) return;
  (instance as any)[ATTACHED_SYMBOL] = true;

  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      console.error("[AUTH REQUEST ERROR]:", error);
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const url = error.config?.url || "";

      const isAuthEndpoint = /\/auth\/(login|verify)/i.test(url);

      if ((status === 401 || status === 403) && !isAuthEndpoint) {
        console.warn(`[AUTH] ${status} detected on ${url} — logging out`);
        localStorage.removeItem("auth_token");
        window.location.replace("/login");
      }
      return Promise.reject(error);
    }
  );
};

attachAuth(api);
attachAuth(almiApi);

const mapToProject = (item: BackendProject): Project => ({
  id: item.ip,
  initial: item.initial,
  alfan: item.ip,
});

const mapToServer = (item: BackendServer): Server => ({
  id: item.alfan,
  alfan: item.alfan,
});

export const verifyAuthToken = async (): Promise<boolean> => {
  const token = localStorage.getItem("auth_token");
  if (!token) return false;

  try {
    const { data } = await api.post<{ success: boolean }>("/auth/verify", { token });
    return data.success === true;
  } catch (err) {
    console.error("Token verification failed:", err);
    localStorage.removeItem("auth_token");
    return false;
  }
};

export const authLogin = async (credentials: { username: string; pass: string }): Promise<LoginResponse> => {
  const { data } = await api.post<LoginResponse>("/auth/login", credentials);
  localStorage.setItem("auth_token", data.token);
  return data;
};

export const fetchProjects = async (): Promise<Project[]> => {
  const { data } = await almiApi.get<BackendProject[]>("/projects");
  return data.map(mapToProject);
};

export const fetchServers = async (): Promise<Server[]> => {
  const { data } = await almiApi.get<BackendServer[]>("/servers");
  return data.map(mapToServer);
};

export const fetchServerStatus = async (
  server: string,
  dryRun = false
): Promise<ServerStatusResponse> => {
  const username = getUsername();
  const payload = { server, dryRun, username };

  const { data } = await almiApi.post<ServerStatusResponse>("/projects", payload);
  return data;
};

export const recoverProjects = async (
  server: string,
  filtered: string[] = [],
  forceMove = true,
  dryRun = true
): Promise<RecoveryJobResponse> => {
  const username = getUsername();
  const payload = { server, filtered, forceMove, dryRun, username }

  const { data } = await almiApi.post<RecoveryJobResponse>("/projects", payload);
  return data;
};

export const fetchBackupDate = async (): Promise<string> => {
  try {
    const { data } = await almiApi.get<string>("/backup/date", {
      responseType: "text",
    });
    return typeof data === "string" ? data.trim() : "";
  } catch (err) {
    console.warn("Не удалось получить дату бэкапа:", err);
    return "";
  }
};

export default api;